/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import { produce } from "immer";
import { round } from "lodash";
import {
  BillingPackage,
  BillingPackageDocument,
  BillingPackageItem
} from "../interfaces/BillingPackages";
import { ServiceInterface } from "../interfaces/ServiceInterface";
import { BILLING_SOURCES } from "../containers/Reports/Filters";
import { ProductType } from "../interfaces/ProductInterface";

export enum ITEM_TYPES {
  PACKAGED = "packaged",
  NON_PACKAGED = "nonPackaged",
  ADDITIONAL = "additional"
}

export const distributeDiscount = (billItems, discountPct): unknown =>
  produce(billItems, (draft) => {
    const invoiceTotal = Object.values(ITEM_TYPES).reduce(
      (acc, section) =>
        acc +
        draft[section].reduce(
          (sum, item) => sum + Number(item.grossTotalPrice || item.grossTotal || 0),
          0
        ),
      0
    );

    if (invoiceTotal === 0) return;

    const totalDiscount = (invoiceTotal * discountPct) / 100;
    let distributed = 0;

    Object.values(ITEM_TYPES).forEach((section) => {
      draft[section].forEach((item, itemIndex) => {
        const itemTotal = Number(item.grossTotalPrice || item.grossTotal || 0);

        const isLastSection = section === "additional";
        const isLastItem = itemIndex === draft[section].length - 1;

        let discountAmt;
        if (isLastSection && isLastItem) {
          discountAmt = totalDiscount - distributed;
        } else {
          discountAmt = (itemTotal / invoiceTotal) * totalDiscount;
          distributed += discountAmt;
        }

        item.discountAmt = discountAmt;
        item.netAmount = itemTotal - discountAmt;
        item.payingAmount = 0; // reset paying amount
      });
    });
  });

export const distributePayingAmount = (
  document: BillingPackageDocument,
  amount: number
): BillingPackageDocument =>
  produce(document, (draft) => {
    const order = ["nonPackaged", "additional", "packaged"];
    let remaining = amount;

    for (const section of order) {
      for (const item of draft[section as ITEM_TYPES]) {
        const itemTotal = Number(item.netAmount || 0);
        const paidTotal = Number(item.paidAmount || 0);
        if (Number(item.paidAmount !== itemTotal)) {
          if (remaining <= 0) {
            item.payingAmount = 0;
            item.dueAmount = itemTotal;
            // eslint-disable-next-line no-continue
            continue;
          }

          if (remaining >= itemTotal) {
            item.payingAmount = itemTotal;
            item.dueAmount = 0;
            remaining -= itemTotal;
          } else {
            item.payingAmount = remaining;
            item.dueAmount = itemTotal - paidTotal - remaining;
            remaining = 0;
          }
        }
      }
    }
  });

export const updatePayingAmountForItem = (
  data: BillingPackage,
  section: ITEM_TYPES,
  itemIndex: number,
  payingAmount: number
): BillingPackage =>
  produce(data, (draft) => {
    const item = draft.document[section][itemIndex];

    const remainingDue = Number(item.netAmount - item.paidAmount || 0);

    item.payingAmount = payingAmount;
    item.dueAmount = remainingDue - payingAmount || 0;
    draft.dueAmount = Number(draft.dueAmount) - payingAmount;
  });

export const updateDiscountForItem = (
  data: BillingPackage,
  section: ITEM_TYPES,
  itemIndex: number,
  discountPercent: number
): BillingPackage =>
  produce(data, (draft) => {
    const item = draft.document[section][itemIndex];

    const gross = Number(item.grossTotal || 0);
    const paidAmount = Number(item.paidAmount || 0);

    item.discountAmt = (gross * discountPercent) / 100;
    item.discountPercent = Number(discountPercent);

    item.netAmount = gross - item.discountAmt;
    if (section === ITEM_TYPES.NON_PACKAGED) {
      item.dueAmount = 0;
      item.payingAmount = item.netAmount;
    } else {
      item.dueAmount = item.netAmount - paidAmount;
    }
  });

export const updateDiscountForItemInRupee = (
  data: BillingPackage,
  section: ITEM_TYPES,
  itemIndex: number,
  discountRs: number
): BillingPackage =>
  produce(data, (draft) => {
    const item = draft.document[section][itemIndex];

    const gross = Number(item.grossTotal || 0);
    const paidAmount = Number(item.paidAmount || 0);

    const discountAmt = Math.min(Number(discountRs || 0), gross);

    const discountPct = gross > 0 ? (discountAmt / gross) * 100 : 0;

    item.discountAmt = discountAmt;
    item.discountPercent = Number(discountPct.toFixed(2));

    item.netAmount = gross - discountAmt;
    if (section === ITEM_TYPES.NON_PACKAGED) {
      item.dueAmount = 0;
      item.payingAmount = item.netAmount;
    } else {
      item.dueAmount = item.netAmount - paidAmount;
    }
  });

export const getTotalPayingAmount = (billItems: BillingPackageDocument): number => {
  const sections = Object.values(ITEM_TYPES);

  return sections.reduce(
    (total, section) =>
      total +
      billItems[section].reduce((sum, item) => {
        const itemPaying = Number(item.payingAmount || 0);
        return sum + itemPaying;
      }, 0),
    0
  );
};
export const getBillItem = ({
  service,
  index,
  customPrice = 0
}: {
  service: Partial<ServiceInterface>;
  index: number;
  serviceProviderId?: number;
  customPrice?: number;
  note?: string;
  teeth?: Array<string>;
}): BillingPackageItem => {
  const vatAmount = round(
    (Number(service?.servicePriceExcVAT) || 0) * ((Number(service?.vatPct) || 0) / 100),
    2
  );

  return {
    calculationBasis: "perUnit",
    delivered: true,
    departmentId: null,
    dueAmount: Number(customPrice || service?.grossTotalPrice) || 0,
    discountAmt: 0,
    discountPercent: 0,
    category: service?.category || ProductType.others,
    expiryDate: null,
    grossTotal: Number(customPrice || service?.grossTotalPrice) || 0,
    quantity: 1,
    nonDiscountable: false,
    salesLedgerId: service?.salesLedgerId,
    salesTaxationId: service?.salesTaxationId,
    serviceProviderRateUnit: "percentage",
    paidAmount: 0,
    payingAmount: 0,
    stockUnitType: "unit",
    subItems: null,
    vatAmtAfterDiscount: vatAmount,
    vatAmtBeforeDiscount: vatAmount,
    sNo: index + 1,
    unit: "pcs",
    source: BILLING_SOURCES.STOCKS,
    vatPct: Number(service?.vatPct) || 0,
    perUnit: Number(customPrice || service?.servicePriceExcVAT) || 0,
    productId: service?.id,
    description: service?.name || "",
    isPaid: false,
    netAmount: Number(customPrice || service?.servicePriceExcVAT) || 0,
    note: ""
  };
};

export const addBillingPackageItem = ({
  packageData,
  item
}: {
  packageData: BillingPackage;
  item?: any;
}) =>
  produce(packageData, (draft) => {
    draft.document.additional.push({
      ...getBillItem({
        service: item,
        index: 1
      }),
      isPaid: false,
      paidAmount: 0,
      dueAmount: item?.grossTotalPrice || 0
    });
  });

export const getTaxAmtWithRates = (billItems: BillingPackageItem[]): Record<string, number> =>
  billItems?.reduce(
    (acc, item) => ({
      ...acc,
      [item.vatPct]: acc[item.vatPct]
        ? acc[item.vatPct] + (item.vatAmtAfterDiscount || 0)
        : item.vatAmtAfterDiscount || 0
    }),
    {}
  );

export const getSumInfo = (
  billItems: BillingPackageItem[]
): { totalAmount: number; taxableAmount: number; taxAmount: number } =>
  billItems.reduce(
    (acc, item) => {
      // eslint-disable-next-line no-param-reassign
      acc.totalAmount += Number(item.grossTotal || 0);
      // eslint-disable-next-line no-param-reassign
      acc.taxableAmount += Number(item.perUnit || 0);
      // eslint-disable-next-line no-param-reassign
      acc.taxAmount += Number(item.vatAmtAfterDiscount || 0);
      return acc;
    },
    { totalAmount: 0, taxableAmount: 0, taxAmount: 0 }
  );

export enum BillingPackageStatusType {
  UNPAID = "unpaid",
  PARTIALLY_PAID = "partiallyPaid",
  COMPLETED = "completed",
  CANCELED = "canceled"
}

export const setPayingAmountByDefaultOnNonPackagedItems = (data: BillingPackage): BillingPackage =>
  produce(data, (draft) => {
    [ITEM_TYPES.ADDITIONAL, ITEM_TYPES.NON_PACKAGED].forEach((section) => {
      draft.document[section]
        .filter((item) => !item.isPaid)
        .forEach((item) => {
          item.payingAmount = item.netAmount;
          item.dueAmount = 0;
        });
    });
  });

export const removeBillItem = ({
  data,
  section,
  itemIndex
}: {
  data: BillingPackage;
  section: ITEM_TYPES;
  itemIndex: number;
}): BillingPackage =>
  produce(data, (draft) => {
    draft.document[section]?.splice(itemIndex, 1);
  });

export const getBillingPackageTotalAmounts = (data: BillingPackageItem[]) => ({
  totalAmount: data?.reduce((prev, cur) => prev + Number(cur.grossTotal || 0), 0),
  totalPaidAmount: data?.reduce((prev, cur) => prev + Number(cur.paidAmount || 0), 0),
  totalDueAmount: data?.reduce((prev, cur) => prev + Number(cur.dueAmount || 0), 0),
  totalNetAmount: data?.reduce((prev, cur) => prev + Number(cur.netAmount || 0), 0)
});

export const amountToRs = (amount: number, decimalPlaces: number = 2) =>
  `Rs. ${amount.toFixed(decimalPlaces)}`;
